// Charges and refunds as the guest receipt, payment and verification pages
// list them under the reservation total.
//
// `booking.price_paid` is maintained server-side as
//   sum(amount_charged of charges in SETTLED_CHARGE_STATUSES) - sum(refunds)
// so the rows a page lists must come from the same statuses for
// "Total - payments + refunds" to land on "Remaining".

// Statuses that moved guest money. A refunded charge stays listed because the
// refund is listed alongside it; `auth`, `expired` and `failed` never settled.
// `payout` is the channel's net remittance on channel-collected bookings, not
// a guest payment, and Direct 2.0 keeps it out of `price_paid` for the same
// reason.
const SETTLED_CHARGE_STATUSES = ['charged', 'partially_refunded', 'refunded'];

const REFUNDED_CHARGE_STATUSES = ['partially_refunded', 'refunded'];

const toAmount = value => {
  const amount = parseFloat(value);
  return Number.isFinite(amount) ? amount : 0;
};

const roundCents = amount => Math.round(amount * 100) / 100;

// Security-deposit holds are tracked separately from the reservation balance.
const isSettledGuestCharge = charge =>
  Boolean(charge) &&
  !charge.is_security_deposit &&
  SETTLED_CHARGE_STATUSES.includes(charge.status);

const settledGuestCharges = charges =>
  Array.isArray(charges) ? charges.filter(isSettledGuestCharge) : [];

const isRefundedCharge = charge =>
  Boolean(charge) && REFUNDED_CHARGE_STATUSES.includes(charge.status);

const settledChargesTotal = charges =>
  roundCents(
    settledGuestCharges(charges).reduce(
      (total, charge) => total + toAmount(charge.amount_charged),
      0
    )
  );

// Refund amounts per se are not part of every guest payload (the verification
// endpoint returns charges and `price_paid` only), but `price_paid` already
// nets them out, so the refunded total is what the settled charges exceed it
// by. Only derived when a listed charge actually carries a refund status.
const derivedRefundTotal = (charges, pricePaid) => {
  const settled = settledGuestCharges(charges);
  if (!settled.some(isRefundedCharge)) return 0;
  const refunded = roundCents(
    settledChargesTotal(settled) - toAmount(pricePaid)
  );
  return refunded > 0 ? refunded : 0;
};

// A refunds list from the API is authoritative, even when empty: a voided
// refund is left out of the list although it still nets out of price_paid, so
// deriving from price_paid would show it as refunded. Only derive when the
// payload has no refunds list at all (the verification endpoint today).
const refundTotal = ({ charges, refunds, pricePaid }) => {
  if (Array.isArray(refunds)) {
    return roundCents(
      refunds.reduce((total, refund) => total + toAmount(refund), 0)
    );
  }
  return derivedRefundTotal(charges, pricePaid);
};

// Wording appended to a charge's payment-method line when part or all of it
// went back to the guest.
const chargeRefundStatusLabel = (charge, translate) => {
  if (!isRefundedCharge(charge)) return null;
  return translate(`cx.receipt.charge_status.${charge.status}`);
};

module.exports = {
  REFUNDED_CHARGE_STATUSES,
  SETTLED_CHARGE_STATUSES,
  chargeRefundStatusLabel,
  derivedRefundTotal,
  isRefundedCharge,
  isSettledGuestCharge,
  refundTotal,
  settledChargesTotal,
  settledGuestCharges
};
